import {
  SchemaField,
  SchemaFieldVariant,
  SchemaFieldSubVariant,
} from "./../featuer-layer-settings/schema.d"
import {
  getPropertyFromAlias,
  stripAdditionalPropertiesFromAlias,
} from "../featuer-layer-settings/esriDefinitionHelpers"

/** Gets HTML input type from Esri field */
export const getHtmlInputType = (type: __esri.Field["type"] | undefined) => {
  switch (type) {
    case "integer":
      return "number"
    case "string":
    default:
      return "text"
  }
}

/**
 * Sanitises any fields that need to be sanitised before sending to Esri
 * @param values The values from the form
 * @param fields The fields the form uses
 */
export const sanitiseForEsri = (
  values: { [field: string]: string | string[] },
  fields: SchemaField[] | undefined
) =>
  Object.entries(values).reduce((result, [key, value]) => {
    // find the schema field to see if we need to do anything to it
    const schemaField = fields?.find((field) => field.name === key)

    // sanitise specific variants
    switch (schemaField?.variant) {
      case "switch":
        return { ...result, [key]: value ? 1 : 0 }
      case "select": {
        const formattedSelectValue =
          schemaField.subvariant === "multiselect" && Array.isArray(value)
            ? (value as string[]).join(", ")
            : value
        return { ...result, [key]: formattedSelectValue }
      }
      default:
        return { ...result, [key]: value ? value : null }
    }
  }, {})

/**
 * Sanitises any fields that need to be sanitised before sending to Esri
 * @param values The values from the form
 * @param fields The fields the form uses
 */
export const parseAttributesForForm = (
  attributes: { [field: string]: string },
  fields: SchemaField[]
) =>
  Object.entries(attributes).reduce((result, [key, value]) => {
    // find the schema field to see if we need to do anything to it
    const schemaField = fields.find((field) => field.name === key)

    // sanitise specific variants
    switch (schemaField?.variant) {
      case "switch":
        return { ...result, [key]: Boolean(value) }
      case "select": {
        const parsedSelectValue =
          schemaField.subvariant === "multiselect" && typeof value === "string"
            ? value.split(", ")
            : value
        return { ...result, [key]: parsedSelectValue }
      }
      default:
        return { ...result, [key]: value ? value : null }
    }
  }, {})

/**
 * Parses fields from esri fields to augmented schema field we can use
 * @param fields the fields to parse
 */
export const parseEsriFields = (fields: __esri.Field[]): SchemaField[] =>
  fields.map((field) => ({
    alias: stripAdditionalPropertiesFromAlias(field.alias),
    name: field.name,
    description: field.description,
    variant: getPropertyFromAlias("variant", field.alias) as SchemaFieldVariant,
    subvariant: getPropertyFromAlias(
      "subvariant",
      field.alias
    ) as SchemaFieldSubVariant,
    required: getPropertyFromAlias("required", field.alias) as boolean,
    options: field
      ?.toJSON()
      .domain?.codedValues?.map((value: { name: string }) => value.name)
      .join(", "),
    length: field.length === -1 ? 128 : field.length,
  }))
